
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TaxonomyLevel } from '@/types/supabase';

export const useReflection = (moduleId: string, taxonomyLevel: TaxonomyLevel, lessonId?: string) => {
  const { user } = useAuth();
  const [reflectionText, setReflectionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingReflection, setExistingReflection] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  // Load existing reflection
  useEffect(() => {
    const checkExistingReflection = async () => {
      if (!user || !moduleId) return;
      
      try {
        const query = supabase
          .from('learning_reflections')
          .select('reflection_text')
          .eq('user_id', user.id)
          .eq('module_id', moduleId)
          .eq('taxonomy_level', taxonomyLevel);
        
        if (lessonId) {
          query.eq('lesson_id', lessonId);
        }
        
        const { data, error } = await query.single();
        
        if (error && error.code !== 'PGRST116') { // Not found error
          throw error;
        }
        
        if (data) {
          setExistingReflection(data.reflection_text);
          setReflectionText(data.reflection_text);
        }
      } catch (error) {
        console.error('Error checking for existing reflection:', error);
      }
    };
    
    checkExistingReflection();
  }, [user, moduleId, lessonId, taxonomyLevel]);

  // Submit reflection
  const submitReflection = async () => {
    if (!user || !moduleId || !reflectionText.trim()) return;
    
    try {
      setIsSubmitting(true);
      
      const reflectionData = {
        user_id: user.id,
        module_id: moduleId,
        taxonomy_level: taxonomyLevel,
        reflection_text: reflectionText,
        ...(lessonId && { lesson_id: lessonId })
      };
      
      if (existingReflection) {
        // Update existing reflection
        const query = supabase
          .from('learning_reflections')
          .update({ reflection_text: reflectionText })
          .eq('user_id', user.id)
          .eq('module_id', moduleId)
          .eq('taxonomy_level', taxonomyLevel);
        
        if (lessonId) {
          query.eq('lesson_id', lessonId);
        }
        
        const { error } = await query;
        
        if (error) throw error;
      } else {
        // Create new reflection
        const { error } = await supabase
          .from('learning_reflections')
          .insert([reflectionData]);
        
        if (error) throw error;
      }
      
      setExistingReflection(reflectionText);
      setShowForm(false);
      
      toast({
        title: "Reflection submitted",
        description: "Your reflection has been saved successfully.",
      });
    } catch (error) {
      console.error('Error submitting reflection:', error);
      toast({
        title: "Error submitting reflection",
        description: "There was a problem saving your reflection.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    reflectionText,
    setReflectionText,
    isSubmitting,
    existingReflection,
    showForm,
    setShowForm,
    submitReflection
  };
};
